import React, { useEffect, useState, useMemo } from 'react';
import { userAppContext } from '../../../../../context/Context';

import TableColumnsMenu from '../menu/TableColumnsMenu';
import { EVIDENCE_LEGEND, restrictedChemicalColumns } from '../../constants';

import RenderInfoLogic from '../../../../../components/InfoSidebar/RenderInfoLogic';
import { getAllColumns } from '../../../../../components/Table/columns/index';
import { ColumnVisibilityModels } from '../../../../../components/Table/ColumnVisibilityModels';

import RenderSourcesLogic from '../source/RenderSourcesLogic';
import SharedButton from '../../../../../global/Button/Button';
import { UserContext } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';
import Alert from '../../../../../global/Alert/Alert';
import { isSourceEnabledForTab } from '../../helpers/sourceListHelpers';
import { EvidenceParams, evidenceSummaryRequest } from '../../helpers/tableHelpers';
import { deleteFilter } from '../../../../../services/savedFiltersService';
import SavedColumnsBar from '../../../../../components/UI/SavedColumnsBar';

// Hooks
import { useColumnManagement } from './hooks/useColumnManagement';
import { useAlertSystem } from './hooks/useAlertSystem';
import { useSaveOperations } from './hooks/useSaveOperations';
import { useConfigurationLoader } from './hooks/useConfigurationLoader';
import { useDownloadOperations } from './hooks/useDownloadOperations';

// Components
import { SaveSearchModal } from './components/SaveSearchModal';
import { SaveColumnsModal } from './components/SaveColumnsModal';
import { SaveScoreModal } from './components/SaveScoreModal';
import { GridToolbar } from './components/GridToolbar';
import { AcademicRestrictions } from './components/AcademicRestrictions';
import { GridContainer } from './components/GridContainer';

// Types
import { ManageGridProps } from './types';

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';

function ManageGrid({
  isMapping,
  mappingContent,
  restrictChemical,
  view,
  tab,
  isEvidence,
  source,
  searchItems,
  setSearchItems,
  isInfoSidebarOpen,
  setIsInfoSidebarOpen,
  dispatch,
  defaultFilterValue,
  sourceWeights = [],
}: ManageGridProps) {
  const sourceCurated = [
    'CURATED',
    'CLINGEN',
    'CLINPGX',
    'CLINVAR',
    'GENCC',
    'MGD_HUMAN',
    'ORPHANET',
    'PSYGENET',
    'RGD_HUMAN',
    'UNIPROT',
    'GWASCAT',
    'PHEWASCAT',
  ];
  const { authorization, isAuthenticated, selectedInfoItem, restrictSources, user } =
    userAppContext() as unknown as UserContext;

  const allColumns = getAllColumns();

  const [totalResults, setTotalResults] = useState(0);
  const getFilterValueRef = React.useRef<any>(null);

  // Column management
  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnOrder,
    setColumnOrder,
    hasLoadedFromSavedRef,
    resetToDefault,
  } = useColumnManagement({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
    restrictChemical,
  });

  // Alert system
  const {
    showAlert,
    alertType,
    showSuccessAlert,
    showErrorAlert,
    showWarningAlert,
    hideAlert,
    getAlertMessage,
  } = useAlertSystem();

  // Save operations
  const saveOperations = useSaveOperations({
    searchItems,
    view,
    tab,
    source,
    isEvidence,
    totalResults,
    columnVisibilityModel,
    columnOrder,
    sourceWeights,
    getFilterValueRef,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Download operations
  const downloadOperations = useDownloadOperations({
    view,
    source,
    tab,
    searchItems,
    isAuthenticated,
    authorization,
    isEvidence,
    isMapping,
    mappingContent,
    totalResults,
    columnVisibilityModel,
    getFilterValueRef,
    dispatch,
    sourceWeights,
    onWarning: showWarningAlert,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Configuration loader
  useConfigurationLoader({
    setColumnVisibilityModel,
    setColumnOrder,
    hasLoadedFromSavedRef,
    dispatch,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Local state
  const [isColumnSelectOpen, setIsColumnSelectOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('tableColumnOpen');
    return saved === 'true';
  });
  const [sourceOpen, setSourceOpen] = useState(false);
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [refreshColumnsBar, setRefreshColumnsBar] = useState(0);
  const [areEvidenceSelectedRows, setEvidenceSelectedRows] = useState(false);
  const [evidenceSummParams, setEvidenceSummParams] = useState<EvidenceParams[]>([]);

  // User permissions
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);
  const isAcademic = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'][0] === 'ROLE_ACADEMIC';
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');

  const isChemicalRestricted = isAuthenticated && isAcademic;

  const shouldShowMessage =
    isChemicalRestricted &&
    (tab === 'GDA' || tab === 'VDA') &&
    Object.keys(columnVisibilityModel).some(key => restrictedChemicalColumns.includes(key));

  // Effects
  useEffect(() => {
    localStorage.setItem('tableColumnOpen', isColumnSelectOpen ? 'true' : 'false');
  }, [isColumnSelectOpen, tab]);

  useEffect(() => {
    const searchAndMappingItems = searchItems ? searchItems.concat(mappingContent?.mappingItems || []) : [];
    if (isInfoSidebarOpen && searchItems && !searchItems.find(item => item.id === selectedInfoItem.id)) {
      if (!searchAndMappingItems.find(item => item.id === selectedInfoItem.id)) {
        setIsInfoSidebarOpen(false);
      }
    }
  }, [searchItems, selectedInfoItem, mappingContent?.mappingItems, isInfoSidebarOpen, setIsInfoSidebarOpen]);

  // Handlers
  const handleOpenMenus = (menu: string) => {
    setIsColumnSelectOpen(prev => (menu === 'columns' ? !prev : false));
    setSourceOpen(prev => (menu === 'source' ? !prev : false));
  };

  const handleDataFromChild = (data: boolean, params: EvidenceParams[]) => {
    setEvidenceSelectedRows(data);
    setEvidenceSummParams(params);
  };

  const handleEvidenceSummClick = async () => {
    const content = await evidenceSummaryRequest(evidenceSummParams, dispatch);
    dispatch({
      type: 'SET_EVIDENCE_MODAL_DATA',
      payload: evidenceSummParams,
    });
    await dispatch({ type: 'SET_EVIDENCE_MODAL_CONTENT', payload: content });
    dispatch({ type: 'SET_SHOW_EVIDENCE_MODAL', payload: true });
  };

  const handleResetToDefault = () => {
    resetToDefault();
    showSuccessAlert('Columns reset to default');
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      const baseModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

      if (baseModel) {
        const mergedModel = { ...baseModel };
        Object.keys(savedColumn.columnVisibilityModel).forEach(key => {
          if (key in mergedModel) {
            mergedModel[key] = savedColumn.columnVisibilityModel[key];
          }
        });
        setColumnVisibilityModel(mergedModel);
      } else {
        setColumnVisibilityModel(savedColumn.columnVisibilityModel);
      }
    }

    if (savedColumn.columnOrder) {
      setColumnOrder(savedColumn.columnOrder);
    }

    showSuccessAlert(`Column configuration "${savedColumn.name}" applied successfully`);
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) {
        await deleteFilter(columnId);
      }
      showSuccessAlert('Column configuration deleted successfully');
      setRefreshColumnsBar(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting column configuration:', error);
      showErrorAlert('Error deleting column configuration');
    }
  };

  // Memoized columns
  const columns = useMemo(() => {
    if (!columnVisibilityModel) return null;

    const originalModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
    if (!originalModel) return null;

    const orderedColumns = [];
    const orderedKeys = Object.keys(columnVisibilityModel);

    orderedKeys.forEach(key => {
      if (columnVisibilityModel[key] === true) {
        const column = allColumns.find(col => col.name === key);
        if (column && (column.name !== 'sourceScore' || sourceWeights.length > 0)) {
          // Evaluar la propiedad show
          let shouldShow = true;
          if (typeof column.show === 'function') {
            shouldShow = column.show({ tab, view, isEvidence });
          } else if (column.show !== undefined) {
            shouldShow = column.show;
          }

          if (shouldShow) {
            orderedColumns.push(column);
          }
        }
      }
    });

    return orderedColumns;
  }, [columnVisibilityModel, sourceWeights, tab, view, isEvidence, allColumns]);

  // Early return for academic chemical restriction
  const academicChemicalRestriction = (
    <AcademicRestrictions
      isAcademic={isAcademic}
      isAuthenticated={isAuthenticated}
      tab={tab}
      source={source}
      sourceCurated={sourceCurated}
      sourceOpen={sourceOpen}
    />
  );

  if (isAcademic && isAuthenticated && tab === 'CHEMICALS') {
    return academicChemicalRestriction;
  }

  if (!columns) return null;

  return (
    <>
      {/* Alert System */}
      {showAlert && (
        <div
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert
            message={getAlertMessage(
              saveOperations.isSavingSearch,
              saveOperations.isSavingColumns,
              false,
              saveOperations.isSavingScore,
            )}
            type={alertType}
            onClose={hideAlert}
          />
        </div>
      )}

      <div>
        {shouldShowMessage && (
          <p className="mt-4 text-pink">
            Some columns are not available in your current academic plan.
            <a
              href="/plans"
              className="text-blue-600 hover:text-blue-800 underline ml-1 font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Upgrade your plan
            </a>
            .
          </p>
        )}
      </div>

      {/* Download Confirmation Modal */}
      {downloadOperations.showDownloadConfirm && (
        <SharedDialog
          isOpen={downloadOperations.showDownloadConfirm}
          onClose={() => downloadOperations.setShowDownloadConfirm(false)}
          title="Large file download warning"
          description="The file you are requesting contains more than 55,000 records. Downloads are limited to this number. We suggest using the REST API for this request."
          confirmText="Yes"
          cancelText="No"
          hasConfirmBtn
          onConfirm={downloadOperations.confirmLargeDownload}
          data-testid="modal"
        >
          <p className="font-medium text-gray-900 mt-2">Do you want to proceed?</p>
        </SharedDialog>
      )}

      {/* Save Modals */}
      <SaveSearchModal
        isOpen={saveOperations.showSaveSearchModal}
        onClose={() => {
          saveOperations.setShowSaveSearchModal(false);
          saveOperations.setSearchName('');
        }}
        onConfirm={saveOperations.confirmSaveSearch}
        searchName={saveOperations.searchName}
        setSearchName={saveOperations.setSearchName}
        searchDataToSave={saveOperations.searchDataToSave}
        isSaving={saveOperations.isSavingSearch}
      />

      <SaveColumnsModal
        isOpen={saveOperations.showSaveColumnsModal}
        onClose={() => {
          saveOperations.setShowSaveColumnsModal(false);
          saveOperations.setColumnSelectionName('');
        }}
        onConfirm={saveOperations.confirmSaveColumnSelection}
        columnSelectionName={saveOperations.columnSelectionName}
        setColumnSelectionName={saveOperations.setColumnSelectionName}
        columnDataToSave={saveOperations.columnDataToSave}
        isSaving={saveOperations.isSavingColumns}
      />

      <SaveScoreModal
        isOpen={saveOperations.showSaveScoreModal}
        onClose={() => {
          saveOperations.setShowSaveScoreModal(false);
          saveOperations.setScoreName('');
        }}
        onConfirm={saveOperations.confirmSaveScore}
        scoreName={saveOperations.scoreName}
        setScoreName={saveOperations.setScoreName}
        sourceWeights={sourceWeights}
        view={view}
        tab={tab}
        isSaving={saveOperations.isSavingScore}
      />

      {/* Main Content */}
      <div className="Cabtabla bg-white mt-3">
        {searchItems && (
          <>
            <div className="row">
              <RenderInfoLogic
                searchItems={searchItems}
                selectedInfoItem={selectedInfoItem}
                setSearchItems={setSearchItems}
                isMapping={isMapping}
              />

              <GridToolbar
                isColumnSelectOpen={isColumnSelectOpen}
                onToggleColumns={() => handleOpenMenus('columns')}
                sourceOpen={sourceOpen}
                onToggleSource={() => handleOpenMenus('source')}
                source={source}
                sourceWeights={sourceWeights}
                showSaveDropdown={showSaveDropdown}
                setShowSaveDropdown={setShowSaveDropdown}
                onSaveComplete={saveOperations.handleSaveComplete}
                onSaveColumnSelection={saveOperations.handleSaveColumnSelection}
                onSaveScore={saveOperations.handleSaveScore}
                isSaving={saveOperations.isSavingSearch || saveOperations.isSavingColumns}
                onResetToDefault={handleResetToDefault}
                isAuthenticated={isAuthenticated}
                isAcademic={isAcademic}
                isTrial={isTrial}
                view={view}
                tab={tab}
                isEvidence={isEvidence}
                isMapping={isMapping}
                mappingContent={mappingContent}
                columnVisibilityModel={columnVisibilityModel}
                columnOrder={columnOrder}
                getFilterValueRef={getFilterValueRef}
                searchItems={searchItems}
                onDownload={downloadOperations.handleDownloadFilesWithFilters}
              />

              <div>
                {sourceOpen && (
                  <RenderSourcesLogic isScoreSource={false} selectedSourceWeights={sourceWeights} />
                )}
                {academicChemicalRestriction}
              </div>
            </div>

            {isColumnSelectOpen && (
              <TableColumnsMenu
                view={view}
                tab={tab}
                setIsColumnSelectOpen={setIsColumnSelectOpen}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
              />
            )}

            <div className="titEtiqueta">
              <span>{isEvidence ? 'Evidence' : 'Summary'}</span>
            </div>
          </>
        )}
      </div>

      {isEvidence && (
        <div className="flex justify-between">
          {EVIDENCE_LEGEND}
          {areEvidenceSelectedRows && SHOW_SUMMARIZE && (
            <div>
              <SharedButton onClick={handleEvidenceSummClick}>Summarize</SharedButton>
            </div>
          )}
        </div>
      )}

      {/* Saved Columns Bar */}
      <SavedColumnsBar
        key={refreshColumnsBar}
        view={view}
        tab={tab}
        isEvidence={isEvidence}
        onApplyColumns={handleApplySavedColumns}
        onDeleteColumn={handleDeleteSavedColumn}
      />

      {/* Academic restrictions and Grid */}
      {isAcademic && !sourceCurated.includes(source) ? (
        <div className="text-center text-gray-500 p-6">
          <h3>Your free academic plan includes curated sources only.</h3>
          <h5>Select your available sources here.</h5>
        </div>
      ) : (
        <GridContainer
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          view={view}
          source={source}
          tab={tab}
          searchItems={searchItems}
          isEvidence={isEvidence}
          isMapping={isMapping}
          setTotalResults={setTotalResults}
          onData={handleDataFromChild}
          getFilterValueRef={getFilterValueRef}
          defaultFilterValue={defaultFilterValue}
          sourceWeights={sourceWeights}
          isInvalidSource={isInvalidSource}
        />
      )}
    </>
  );
}

export default ManageGrid;
